import { Button } from '@apollo/orbit';
import getIsChromatic from 'chromatic/isChromatic';
import classnames from 'classnames';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import 'monaco-editor/esm/vs/editor/contrib/suggest/browser/suggestController';
import { TextModel } from 'monaco-editor/esm/vs/editor/common/model/textModel';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactResizeDetector from 'react-resize-detector';

import { useEditorModel } from 'src/app/graph/explorerPage/hooks/useEditorModel';
import {
  MonacoLanguage,
  createNewModel,
  defineStudioMonacoThemes,
  useRegisterMonacoLanguageContribution,
  useSetMonacoTheme,
} from 'src/app/graph/shared/monacoHelpers/monacoHelpers';
import {
  SchemaInfo,
  useApplyJSONSchema,
} from 'src/components/editor/useApplyJSONSchema';

import { useRemeasureFontsForMonaco } from './useRemeasureFontsForMonaco';

defineStudioMonacoThemes();

export const MONACO_READONLY_OPTIONS: monacoEditor.editor.IEditorConstructionOptions & {
  lineNumbers: 'off';
} = {
  contextmenu: false,
  lineNumbers: 'off',
  readOnly: true,
  glyphMargin: false,
};

// Pull this out so we can still enforce a type here. The type of the prop
// is the more general IEditorConstructionOptions
export const DEFAULT_MONACO_OPTIONS:
  | monacoEditor.editor.IStandaloneEditorConstructionOptions
  | (monacoEditor.editor.IDiffEditorConstructionOptions & {
      // The setting to turn off bracket pair colorization is broken through at least 0.44.0
      // https://github.com/microsoft/monaco-editor/issues/3829
      // TODO: Remove this when fixed
      'bracketPairColorization.enabled': boolean;
    }) = {
  tabSize: 2,
  wordBasedSuggestions: false,
  selectOnLineNumbers: true,
  scrollBeyondLastLine: false,
  tabCompletion: 'on',
  overviewRulerLanes: 0,
  fontFamily: 'var(--font-mono)',
  fontSize: 14,
  renderLineHighlight: 'none',
  wordWrap: 'on',
  scrollbar: {
    useShadows: false,
    verticalScrollbarSize: 6,
    horizontalScrollbarSize: 6,
  },
  fixedOverflowWidgets: true,
  'semanticHighlighting.enabled': true,
  'bracketPairColorization.enabled': false,
};

export function getFileOverFormattingThreshold(value: string) {
  return value.length > TextModel.LARGE_FILE_SIZE_THRESHOLD;
}
export const useStudioEditor = ({
  overrideServices,
  language,
  defaultValue,
  lineNumbers = 'on',
  readOnly = false,
  contextmenu,
  glyphMargin = true,
  lineDecorationsWidth,
  minimap = { enabled: false },
  includeTopMargin = true,
  lineGutterPaddingSpace = 0,
  onChange = _.noop,
  createDefaultModel = true,
}: Pick<
  monacoEditor.editor.IStandaloneEditorConstructionOptions,
  | 'contextmenu'
  | 'glyphMargin'
  | 'readOnly'
  | 'cursorBlinking'
  | 'lineDecorationsWidth'
  | 'showFoldingControls'
  | 'minimap'
> & {
  createDefaultModel?: boolean;
  lineNumbers?: 'on' | 'off';
  language: MonacoLanguage;
  overrideServices?: monacoEditor.editor.IEditorOverrideServices;
  defaultValue?: string;
  includeTopMargin?: boolean;
  onChange?: (
    newValue: string,
    event: monacoEditor.editor.IModelContentChangedEvent,
  ) => void;
  lineGutterPaddingSpace?: number;
}): monacoEditor.editor.IStandaloneCodeEditor => {
  useRegisterMonacoLanguageContribution(language);
  useSetMonacoTheme();
  useRemeasureFontsForMonaco();

  const [lineNumbersMinChars, setLineNumbersMinChars] = useState(5);

  const options = useMemo(
    () => ({
      lineNumbers,
      readOnly,
      lineDecorationsWidth,
      lineNumbersMinChars,
      contextmenu,
      glyphMargin,
      minimap,
    }),
    [
      lineNumbers,
      readOnly,
      lineDecorationsWidth,
      lineNumbersMinChars,
      contextmenu,
      glyphMargin,
      minimap,
    ],
  );
  // store options in ref so we can compare and skip the first update, the editor init applies them.
  const optionsRef = useRef(options);

  const editorId = useMemo(() => _.uniqueId(), []);

  const fileOverFormattingThreshold = getFileOverFormattingThreshold(
    defaultValue ?? '',
  );
  const fileOverSyncThreshold =
    (defaultValue?.length ?? 0) > TextModel.MODEL_SYNC_LIMIT;

  const modelId = useMemo(() => {
    // syncing is the largest threshold, so we check if we are over it first
    if (fileOverSyncThreshold) {
      return `value-too-large-to-syncing-file`;
    } else if (fileOverFormattingThreshold) {
      return `value-too-large-to-tokenize-file`;
    } else {
      return 'value-can-be-tokenize-file';
    }
  }, [fileOverFormattingThreshold, fileOverSyncThreshold]);

  // These options should not cause the editor to remount, so we store them in a
  // ref to avoid needing them in the dependency array.
  const monacoInitOptions = {
    language,
    defaultValue,
    overrideServices,
    modelId,
    editorId,
    createDefaultModel,
  };
  const monacoInitOptionsRef = useRef(monacoInitOptions);
  monacoInitOptionsRef.current = monacoInitOptions;

  const editor = useMemo<monacoEditor.editor.IStandaloneCodeEditor>(() => {
    const node = document.createElement('div');
    node.className = 'w-full h-full';
    node.dataset.chromatic = 'ignore'; // editor contents is shifting in chromatic. Disable monaco in chromatic.
    return monacoEditor.editor.create(
      node,
      {
        ...DEFAULT_MONACO_OPTIONS,
        ...optionsRef.current,
        language: monacoInitOptionsRef.current.language,
        value: monacoInitOptionsRef.current.defaultValue,
        model: monacoInitOptionsRef.current.createDefaultModel
          ? createNewModel(
              {
                id: monacoInitOptionsRef.current.modelId,
                text: monacoInitOptionsRef.current.defaultValue ?? '',
                language: monacoInitOptionsRef.current.language,
              },
              monacoInitOptionsRef.current.editorId,
            )
          : null,
      },
      monacoInitOptionsRef.current.overrideServices,
    );
  }, []);

  editor.onDidChangeModelContent((event) => {
    onChange(editor.getValue(), event);
  });

  const model: null | monacoEditor.editor.ITextModel = useEditorModel(editor);

  useEffect(() => {
    return () => {
      editor.dispose();
      const currentModel = editor.getModel();
      // If the parent is a tabbed editor it will clean up its own
      // models. If not, clean up here.
      if (currentModel && !currentModel?.isDisposed()) {
        currentModel.dispose();
      }
    };
  }, [editor]);

  useEffect(() => {
    if (createDefaultModel && model && model.getLanguageId() !== language) {
      monacoEditor.editor.setModelLanguage(model, language);
    }
  }, [createDefaultModel, model, language]);

  useEffect(() => {
    if (options !== optionsRef.current) {
      editor.updateOptions(options);
      optionsRef.current = options;
    }
  }, [editor, options]);

  useEffect(() => {
    if (
      createDefaultModel &&
      readOnly &&
      defaultValue !== undefined &&
      editor.getValue() !== defaultValue
    ) {
      const nextModel = createNewModel(
        {
          id: monacoInitOptionsRef.current.modelId,
          text: monacoInitOptionsRef.current.defaultValue ?? '',
          language: monacoInitOptionsRef.current.language,
        },
        monacoInitOptionsRef.current.editorId,
      );
      const currentModel = editor.getModel();
      // If we don't have a value set update to the new default
      editor.setModel(nextModel);
      if (currentModel?.uri.toString(true) !== nextModel.uri.toString(true)) {
        currentModel?.dispose();
      }
    }
  }, [editor, defaultValue, readOnly, language, createDefaultModel]);

  useEffect(() => {
    if (model && !model.isDisposed() && lineNumbers !== 'off') {
      const updateLineNumbersMinChars = () => {
        const numLines = (model.getValue().match(/\n/g) || []).length + 1;
        setLineNumbersMinChars(
          Math.max(String(numLines).length + 1, 3) + lineGutterPaddingSpace,
        );
      };
      updateLineNumbersMinChars();
      const disposable = model.onDidChangeContent(updateLineNumbersMinChars);
      return () => disposable.dispose();
    }
  }, [model, lineNumbers, lineGutterPaddingSpace]);

  useEffect(() => {
    const disposables: monacoEditor.IDisposable[] = [];

    /**
     * (from dman)
     *
     * I feel strongly that we need a top margin
     * on our editor above Line 1 that pushes the code down a little but still
     * scrolls away when you scroll your code down in the Studio editor.
     *
     * Googled around for quite a while and apparently the only way to do this
     * is with a "view zone", per this thread: https://github.com/Microsoft/monaco-editor/issues/1333
     *
     * Very sad there's no Monaco Editor option or CSS option...
     * Anyway, here we are:
     */
    if (includeTopMargin) {
      const addPadding = (
        accessor: monacoEditor.editor.IViewZoneChangeAccessor,
      ) => {
        accessor.addZone({
          afterLineNumber: 0,
          heightInPx: 8, // we choose 8 because it's a standard size of ours
          domNode: document.createElement('SPAN'),
        });
      };
      editor.changeViewZones(addPadding);
      // Whenever the model changes these get reset, add them back
      disposables.push(
        editor.onDidChangeModel(() => editor.changeViewZones(addPadding)),
      );
    }

    // When we open the context menu, we set the editor it belongs to to a
    // higher z index so that it shows above it surroundings
    disposables.push(
      editor.onContextMenu(() => {
        const splitPane = editor.getDomNode()?.closest('.Pane');
        splitPane?.classList.add('z-30');
        const editorPane = editor
          .getDomNode()
          ?.closest('.xxx-monaco-editor-pane');
        editorPane?.classList.add('z-30');

        const menuContainer = editor
          .getDomNode()
          ?.querySelector('.monaco-menu-container');
        if (menuContainer) {
          new MutationObserver((event) => {
            if (
              event.some((record) => record.removedNodes.length) &&
              !menuContainer.childElementCount
            ) {
              editorPane?.classList.remove('z-30');
              splitPane?.classList.remove('z-30');
            }
          }).observe(menuContainer, { childList: true });
        }
      }),
    );

    // Note: Monaco uses the binary representations of keys to describe complex
    // keybindings. See https://github.com/microsoft/monaco-editor/issues/102#issuecomment-831859015
    disposables.push(
      monacoEditor.editor.addKeybindingRules([
        {
          // the default shortcut for expandLineSelection uses CMD + L which we are using for go to line.
          // eslint-disable-next-line no-bitwise
          keybinding: monacoEditor.KeyMod.CtrlCmd | monacoEditor.KeyCode.KeyL,
          command: null,
        },
        {
          // the default shortcut for formatSelection uses CMD + K which we are using for search.
          // eslint-disable-next-line no-bitwise
          keybinding: monacoEditor.KeyMod.CtrlCmd | monacoEditor.KeyCode.KeyK,
          command: null,
        },
      ]),
    );

    return () => disposables?.forEach((disposable) => disposable.dispose());
  }, [editor, includeTopMargin]);

  return editor;
};

export const StudioEditorContainer = ({
  schemaInfo,
  className,
  editor,
  autoSizeHeight = false,
}: {
  schemaInfo?: SchemaInfo;
  className?: string;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  autoSizeHeight?: boolean;
}) => {
  useApplyJSONSchema({ editor, schemaInfo });
  const editorContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const editorContainer = editorContainerRef.current;
    const domNode = editor.getContainerDomNode();
    if (domNode) {
      editorContainer?.appendChild(domNode);
      editor.layout();
      return () => {
        editorContainer?.removeChild(domNode);
      };
    }
  }, [editor]);

  // This allows us to have the editor change it's height based on it's content instead of a fixed height with scrolling
  useEffect(() => {
    if (!autoSizeHeight) {
      return;
    }

    const updateHeight = () => {
      const height = editor.getContentHeight();
      if (editorContainerRef.current) {
        editorContainerRef.current.style.height = `${height}px`;
      }
      editor.layout();
    };
    const disposeable = editor.onDidContentSizeChange(updateHeight);

    editor.updateOptions({
      scrollbar: {
        vertical: 'hidden',
        horizontal: 'hidden',
        handleMouseWheel: false,
      },
      scrollBeyondLastLine: false,
    });

    return () => {
      disposeable.dispose();
    };
  }, [editor, autoSizeHeight]);

  const isChromatic = getIsChromatic();
  return (
    <ReactResizeDetector
      handleWidth
      handleHeight
      onResize={() => {
        // XXX(Prevent Chromatic flakes) This can cause a layout feedback loop that doesn't stabilize deterministically
        if (!isChromatic) {
          editor.layout();
        }
      }}
    >
      <div
        // Even with the monaco node ignored in chromatic, we still get flake
        // from the layout height changing. We've tried a number of fixes here
        // which didn't work. Don't render monaco at all in chromatic.
        ref={isChromatic ? undefined : editorContainerRef}
        className={classnames('xxx-monaco-editor-pane w-full', className, {
          'overflow-auto p-2': isChromatic,
        })}
      >
        {isChromatic ? (
          // render content of editor instead of editor for chromatic
          <>
            {/*
              monaco will autofocus, so this button is here to mimic that
              behaviour in chromatic so we get a prod like initial focus item
            */}
            <Button
              className="cursor-default text-warning"
              autoFocus
              variant="hidden"
            >
              (Monaco hidden to avoid hard to fix chromatic flake.)
            </Button>
            <pre className="whitespace-pre-wrap bg-transparent p-0 font-mono text-sm">
              {editor.getValue()}
            </pre>
          </>
        ) : undefined}
      </div>
    </ReactResizeDetector>
  );
};
